import type {
  YaMetrika,
  YandexMetricConfig,
  YandexMetricOptions,
} from '~/shared/interfaces/yandex-metrics'

let metrikaInstance: YaMetrika | null = null

export const useYandexMetric = () => {
  const mergeWithDefaultOptions = (options: YandexMetricOptions) => {
    const defaultYandexMetricConfigOptions: Omit<YandexMetricOptions, 'id'> = {
      accurateTrackBounce: true,
      clickmap: true,
      defer: false,
      ecommerce: false,
      params: [],
      userParams: {},
      trackHash: false,
      trackLinks: true,
      type: 0,
      webvisor: false,
      triggerEvent: false,
    }

    return {
      ...defaultYandexMetricConfigOptions,
      ...options,
    }
  }

  const createMetric = (
    options: YandexMetricOptions,
    config: YandexMetricConfig
  ): YaMetrika | undefined => {
    if (typeof window === 'undefined') {
      console.warn('[yandex-metric] No access to global window object')
      return
    }

    const metricConstructor = window?.Ya?.Metrika2

    if (!metricConstructor) {
      console.warn('[yandex-metric] Yandex Metrika API not found')
      return
    }

    if (config.env === 'production') {
      const newOptions = mergeWithDefaultOptions(options)
      return new metricConstructor(newOptions)
    } else {
      console.warn('[yandex-metric] Tracking is disabled, because env option is not "production"')

      if (config.debug) {
        console.warn("[yandex-metric] DEBUG is true: you'll see all API calls in the console")
      }

      return {
        addFileExtension(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] addFileExtension:', args)
        },
        extLink(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] extLink:', args)
        },
        file(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] file:', args)
        },
        getClientID(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] getClientID:', args)
          return 'mock-client-id'
        },
        hit(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] hit:', args)
        },
        params(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] params:', args)
        },
        reachGoal(...args: unknown[]) {
          if (config.debug) {
            // eslint-disable-next-line no-console
            console.log('[yandex-metric] reachGoal:', args)
          }
        },
        userParams(...args: unknown[]) {
          // eslint-disable-next-line no-console
          if (config.debug) console.log('[yandex-metric] userParams:', args)
        },
      } as unknown as YaMetrika
    }
  }

  const startTracking = (metric: YaMetrika, config: YandexMetricConfig) => {
    config.router.afterEach((to, from) => {
      metric.hit(to.fullPath, { referer: from.fullPath })
    })
  }

  const register = (options: YandexMetricOptions, config: YandexMetricConfig): void => {
    const metric = createMetric(options, config)
    if (metric) {
      metrikaInstance = metric
      startTracking(metric, config)
    }
  }

  const getMetrika = (): YaMetrika | null => {
    return metrikaInstance
  }

  const reachGoal = (target: string, params?: { order_price?: number; currency?: string }) => {
    if (!metrikaInstance) {
      console.warn('[yandex-metric] Метрика не инициализирована')
      return
    }

    try {
      metrikaInstance.reachGoal(target, params)
    } catch (error) {
      console.error('[yandex-metric] Ошибка при отправке цели:', error)
    }
  }

  return {
    register,
    getMetrika,
    reachGoal,
  }
}
